import {
	BigNum,
	LAMPORTS_EXP,
	MarketStatus,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS, MAIN_POOL_ID, UIMarket } from '@velocity-exchange/common';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	DEFAULT_ACCOUNT_NAMES_BY_POOL_ID,
	MIN_LEFTOVER_SOL,
	ZERO_QUOTE_BIGNUM,
} from 'src/constants/constants';
import Env from 'src/environmentVariables/EnvironmentVariables';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useDepositCollateral } from 'src/hooks/velocityActions/spot-management/useDepositCollateral';
import useTransferableWalletCollateralBalance from 'src/hooks/useTransferableWalletCollateralBalance';
import useVelocityStore from '../../../stores/VelocityStore/useVelocityStore';
import { getDepositValidationPrice } from 'src/utils/depositValidation';
import useOraclePrice from 'src/hooks/useOraclePrice';
import { useGetBiggestAssetMarket } from 'src/hooks/useGetBiggestAssetMarket';
import { useAccountCreationCost } from 'src/hooks/useAccountCreationCost';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import { useCollateralActionGate } from 'src/components/Modals/CollateralModal/hooks/useCollateralActionGate';
import { useDepositDestinationLimits } from 'src/components/Modals/CollateralModal/hooks/useDepositDestinationLimits';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';

import { useQueryClient } from '@tanstack/react-query';
import { useIsSignedMsgUserOrdersAccountInitialized } from 'src/hooks/signedMsgOrders/useIsSignedMsgUserOrdersAccountInitialized';
import { cj } from 'src/utils/classNames';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import { useSyncedDepositInput } from 'src/stores/DepositInputStore/useSyncedDepositInput';
import AirdropButton from 'src/components/Buttons/AirdropButton';
import useVelocityAccountsStore, {
	AccountData,
} from 'src/stores/useVelocityAccountsStore';
import Button from 'src/components/primitives/Button/Button';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import useModal from 'src/components/Modals/hooks/useModal';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import Divider from 'src/components/primitives/Divider/Divider';
import SubAccountSelector from './components/SubAccountSelector';
import { EarnApy } from './components/EarnApy';
import { BalanceChanges } from './components/BalanceChanges';
import { RentDisclaimers } from './components/RentDisclaimers';
import BankruptAccountWarning from './BankruptAccountWarning';
import { useCreateNewSubaccount } from 'src/hooks/velocityActions/user/useCreateNewSubaccount';
import useIsMainnet from 'src/hooks/useIsMainnet';
import {
	prefetchFunXyzAssets,
	prefetchFunXyzDepositAddress,
} from 'src/hooks/useFunXyzActions';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { DepositFromOtherSourcesContent } from './components/DepositFromOtherSourcesContent';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { USDC_ICON_SRC } from 'src/constants/swapAndDeposit';
import { ChevronRight } from '@velocity-exchange/icons';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

const MIN_DEPOSIT_AMOUNT_FOR_NEW_ACCT = Env.minInitialDepositValue;

const DepositModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) => {
	const borrowData = useVelocityStore((s) => s.borrowLendData);
	const { depositCollateralForExistingAccount } = useDepositCollateral();
	const {
		selectedTargetAccountKey,
		selectedTargetAccount,
		selectedPoolId,
		selectedCollateralMarket,
		handleTargetAccountChange,
		defaultToLargestBalance,
	} = useCollateralModalState({
		collateralIndex,
		collateralPoolId,
		targetAccountKey,
		defaultLargestBalance,
	});

	const accountExists = !!selectedTargetAccount;
	const solBalance = useVelocityStore((s) => s.wallet.currentSolBalance);
	const userStatsAccount = useVelocityStore((s) => s.userStatsAccount);
	const { captureEvent } = usePostHogCapture();
	const isWalletConnected = useWalletIsConnected();
	const queryClient = useQueryClient();
	const { data: isSignedMsgUserOrdersAccountInitialized } =
		useIsSignedMsgUserOrdersAccountInitialized();
	const { hide, show, push } = useModal();
	const createNewSubaccount = useCreateNewSubaccount();
	const isMainnet = useIsMainnet();
	const isDepositFromOtherSourcesEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_DEPOSIT_FROM_OTHER_SOURCES')
	);
	const hasMainPoolAccount = useVelocityAccountsStore((s) =>
		Object.values(s.accounts).some(
			(acc) => acc !== undefined && acc.poolId === MAIN_POOL_ID
		)
	);

	const getBiggestAssetMarket = useGetBiggestAssetMarket();
	const biggestAssetMarket = getBiggestAssetMarket(selectedPoolId);

	const { totalCost: totalAccountCreationCost } = useAccountCreationCost();

	const [submitting, setSubmitting] = useState(false);

	const {
		selectedCollatBank,
		depositInputAmount,
		targetAccountKeyInput,
		persistInputAmountRef,
		setSelectedMarket,
		setCollatTransferAmount,
		setPersistInputAmount,
	} = useSyncedDepositInput(
		defaultToLargestBalance,
		biggestAssetMarket,
		selectedCollateralMarket.marketIndex,
		selectedTargetAccountKey
	);

	const filterOutMarketIndices = useMemo(() => {
		return borrowData
			.filter((bank) =>
				ENUM_UTILS.match(
					bank.spotMarketAccount.status,
					MarketStatus.REDUCE_ONLY
				)
			)
			.map((bank) => bank.bankIndex);
	}, [borrowData]);

	const selectedBankUiConfig = UIMarket.createSpotMarket(
		selectedCollatBank.marketIndex
	);

	const [walletDepositableBalanceBigNum, loadingWalletDepositableBalance] =
		useTransferableWalletCollateralBalance(selectedCollatBank, !accountExists);

	const uiMarket = UIMarket.createSpotMarket(selectedCollatBank?.marketIndex);

	const { marketRemainingDeposits, isAccountBankrupt, isEmptyAmplifyAccount } =
		useDepositDestinationLimits({
			market: selectedCollatBank,
			targetAccountKey: selectedTargetAccountKey,
			targetAccount: selectedTargetAccount as AccountData | undefined,
		});

	const maxAllowableDepositBigNum = !marketRemainingDeposits
		? walletDepositableBalanceBigNum
		: BigNum.max(
				BigNum.zero(marketRemainingDeposits.precision),
				BigNum.min(marketRemainingDeposits, walletDepositableBalanceBigNum)
			);

	const collatTransferAmountBigNum = useMemo(
		() =>
			parseInputBigNumOrZero(
				depositInputAmount ? depositInputAmount : '0',
				selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP
			),
		[depositInputAmount, selectedCollatBank?.precisionExp]
	);

	const oraclePrice = useOraclePrice(uiMarket.marketId) ?? ZERO_QUOTE_BIGNUM;

	const validationPrice = getDepositValidationPrice(
		uiMarket.marketIndex,
		oraclePrice
	);

	const effectiveMaxDepositBigNum = maxAllowableDepositBigNum;

	const exceedsMinDeposit =
		collatTransferAmountBigNum
			.shiftTo(QUOTE_PRECISION_EXP)
			.mul(validationPrice)
			.toNum() >= MIN_DEPOSIT_AMOUNT_FOR_NEW_ACCT;

	const isBelowMinDeposit = !exceedsMinDeposit && !userStatsAccount; // only require minimum deposit for new users

	const solRequiredToDeposit = (
		accountExists ? BigNum.zero(LAMPORTS_EXP) : totalAccountCreationCost
	).add(MIN_LEFTOVER_SOL);
	const hasEnoughSolInWallet = solBalance.gte(solRequiredToDeposit);

	// Gate note: the old inline boolean also required
	// `effectiveMaxDepositBigNum.gtZero()`. Once the ceiling is loaded that
	// clause is implied by amount > 0 && amount <= max; while it is loading
	// the gate blocks outright (loading ≠ validation-passed).
	const { canSubmit: allowConfirmation, exceededMax } = useCollateralActionGate(
		{
			amount: collatTransferAmountBigNum,
			amountInput: depositInputAmount,
			maxAmount: effectiveMaxDepositBigNum,
			maxAmountStatus: loadingWalletDepositableBalance ? 'loading' : 'loaded',
			isSubmitting: submitting,
			isWalletConnected,
			extraChecks: [
				{ id: 'BELOW_MIN_DEPOSIT', blocked: isBelowMinDeposit },
				{ id: 'INSUFFICIENT_SOL', blocked: !hasEnoughSolInWallet },
				{ id: 'EMPTY_AMPLIFY_ACCOUNT', blocked: isEmptyAmplifyAccount },
			],
		}
	);

	const handleDeposit = useCallback(async () => {
		setSubmitting(true);

		let success: boolean | undefined;
		try {
			const collateralAmount = parseInputBigNumOrZero(
				depositInputAmount,
				selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP
			);

			if (accountExists && selectedTargetAccount.poolId === selectedPoolId) {
				captureEvent('collateral_deposit_submitted', {
					spot_market_symbol: selectedCollatBank?.symbol,
					newAccount: false,
					depositAmount: collateralAmount.toNum(),
				});

				success = await depositCollateralForExistingAccount(
					collateralAmount,
					selectedCollatBank,
					targetAccountKeyInput,
					false,
					!isSignedMsgUserOrdersAccountInitialized
				);

				if (success && queryClient) {
					queryClient.invalidateQueries({
						queryKey: [
							'isSignedMsgUserOrdersAccountInitialized',
							selectedTargetAccount?.authority?.toBase58(),
						],
					});
				}
			} else {
				captureEvent('collateral_deposit_submitted', {
					spot_market_symbol: selectedCollatBank?.symbol,
					newAccount: true,
					depositAmount: collateralAmount.toNum(),
				});

				success = await createNewSubaccount({
					depositAmount: collateralAmount,
					spotMarketIndex: selectedCollatBank.marketIndex,
					poolId: selectedPoolId,
					// Close the modal the instant creation succeeds, before the
					// account-subscription settling sleeps, to avoid briefly showing
					// the "Not enough SOL" alert. `if (success) hide()` below stays
					// as a safety net.
					onAccountCreated: hide,
				});
			}
		} catch {
			success = false;
		}

		if (success) {
			hide();
		} else {
			setSubmitting(false);
		}
	}, [
		accountExists,
		captureEvent,
		depositInputAmount,
		hide,
		queryClient,
		selectedCollatBank,
		selectedPoolId,
		selectedTargetAccount,
		targetAccountKey,
	]);

	const handleValueChange = (newValue?: string) => {
		// Set input to max amount if exceeds limit
		if (newValue && newValue !== '') {
			// Use selectedCollatBank.precisionExp to match effectiveMaxDepositBigNum precision
			const inputBigNum = parseInputBigNumOrZero(
				newValue,
				selectedCollatBank.precisionExp
			);

			if (inputBigNum.lte(effectiveMaxDepositBigNum)) {
				setCollatTransferAmount(newValue);
			} else {
				// Set to max amount if input exceeds limit
				setCollatTransferAmount(effectiveMaxDepositBigNum.print());
			}
		} else {
			setCollatTransferAmount(newValue ?? '');
		}
	};

	// Prefetch Fun.xyz assets and deposit address for faster loading when opening the other sources modal (only when flag enabled)
	useEffect(() => {
		if (!isDepositFromOtherSourcesEnabled) return;

		prefetchFunXyzAssets().catch(() => {
			// Silently fail - will retry when modal opens
		});

		// Also prefetch deposit address if wallet is connected
		if (selectedTargetAccount?.authority) {
			const recipientAddress = selectedTargetAccount.authority.toBase58();
			const subAccountId = selectedTargetAccount.userId ?? 0;
			prefetchFunXyzDepositAddress(recipientAddress, subAccountId).catch(() => {
				// Silently fail - will retry when modal opens
			});
		}
	}, [
		isDepositFromOtherSourcesEnabled,
		selectedTargetAccount?.authority,
		selectedTargetAccount?.userId,
	]);

	// If the user is not connected, show the connect wallet modal first,
	// then return to the deposit modal.
	useEffect(() => {
		if (isWalletConnected) {
			return;
		}

		const returnToDepositModal = () => {
			show({
				type: 'deposit',
				props: {
					collateralIndex: collateralIndex ?? 0,
					targetAccountKey: targetAccountKey ?? '',
					defaultLargestBalance: defaultToLargestBalance,
				},
			});
		};

		show({
			type: 'connect_wallet',
			props: {
				callback: returnToDepositModal,
			},
		});
	}, [
		isWalletConnected,
		selectedCollatBank,
		selectedPoolId,
		targetAccountKey,
		defaultToLargestBalance,
		show,
		collateralIndex,
	]);

	// If the user has exceeded the max allowable deposit, set the deposit input amount to the max allowable deposit
	useEffect(() => {
		if (exceededMax && isWalletConnected) {
			setCollatTransferAmount(effectiveMaxDepositBigNum.print());
		}
	}, [exceededMax, isWalletConnected]);

	// reset the deposit input amount when market index changes, unless persistInputAmountRef is true
	useEffect(() => {
		if (persistInputAmountRef.current) {
			setPersistInputAmount(false);
			return;
		}
		setCollatTransferAmount('');
	}, [selectedCollatBank?.marketIndex, selectedPoolId]);

	const isMobile = useIsMobileScreenSize();

	if (isAccountBankrupt) {
		return (
			<BaseModal.Container
				onClose={hide}
				dataTestId="deposit-modal-bankrupt-warning"
			>
				<BaseModal.Title
					onClose={hide}
					closeButtonTestId="deposit-modal-bankrupt-warning-close"
				></BaseModal.Title>
				<BaseModal.Body>
					<BankruptAccountWarning />
				</BaseModal.Body>
			</BaseModal.Container>
		);
	}

	const handleDepositFromOtherSources = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		push({
			type: 'deposit_from_other_sources',
			props: {
				collateralIndex,
				collateralPoolId,
				targetAccountKey:
					selectedPoolId === MAIN_POOL_ID ? selectedTargetAccountKey : null,
				defaultLargestBalance,
			},
		});
	};

	const handleSwapAndDeposit = () => {
		push({
			type: 'swap_and_deposit',
			props: {
				targetAccountKey: selectedTargetAccountKey,
				collateralPoolId: selectedPoolId,
			},
		});
	};

	const showSwapAndDeposit = isMainnet && isWalletConnected;

	const showDepositFromOtherSources =
		isDepositFromOtherSourcesEnabled && accountExists && hasMainPoolAccount;

	const showDesktopDepositFromOtherSources =
		!isMobile && showDepositFromOtherSources;

	return (
		<BaseModal.Container
			onClose={hide}
			className={cj(
				showDesktopDepositFromOtherSources &&
					'overflow-visible bg-transparent shadow-none'
			)}
			dataTestId="deposit-modal"
		>
			<div className="flex h-full flex-col overflow-hidden sm:rounded-lg sm:shadow-md">
				<BaseModal.Title onClose={hide} closeButtonTestId="deposit-modal-close">
					Deposit Funds
				</BaseModal.Title>

				<BaseModal.Body>
					{!isMainnet && <AirdropButton spotMarket={selectedCollatBank} />}

					<SubAccountSelector
						mode="deposit"
						newSubaccount={{
							isNewAccount: !accountExists,
							defaultName: DEFAULT_ACCOUNT_NAMES_BY_POOL_ID[selectedPoolId],
						}}
						selectedTargetAccountKey={selectedTargetAccountKey}
						selectedPoolId={selectedPoolId}
						handleTargetAccountChange={handleTargetAccountChange}
						includeDelegates
					/>

					{showSwapAndDeposit && (
						<Button
							size="sm"
							variant="secondary"
							className="gap-x-xs self-start px-4 py-2"
							onClick={handleSwapAndDeposit}
							dataTestId="swap-and-deposit-cta"
						>
							Swap{' '}
							<img src={USDC_ICON_SRC} alt="" className="h-4 w-4 shrink-0" />{' '}
							and Deposit{' '}
							<MarketIcon
								marketSymbol="USDT"
								sizeClass="w-4 h-4"
								className="shrink-0"
							/>
							<ChevronRight size={16} className="shrink-0" />
						</Button>
					)}

					{/** Token/Size Input */}
					<CollateralInputV2
						dataTestId="deposit-modal-amount-input"
						maxAmount={effectiveMaxDepositBigNum}
						selectedMarket={selectedCollatBank}
						onChangeValue={handleValueChange}
						onChangeMarket={setSelectedMarket}
						value={depositInputAmount}
						label="Amount"
						defaultToHighestBalance={defaultToLargestBalance}
						source={{
							type: 'wallet',
							filterOutMarketIndices,
						}}
						customElementRight={
							<InputQuickActions.SizePresets
								onClick={(amount) => setCollatTransferAmount(amount.print())}
								maxAmount={effectiveMaxDepositBigNum}
								isLoading={loadingWalletDepositableBalance}
								testIdPrefix="deposit-modal"
							/>
						}
						poolId={selectedPoolId}
						searchBodyWithApy={true}
						fullScreenPopover={isMobile}
						fullScreenPopoverTitle="Select Asset"
					/>

					<EarnApy
						selectedMarketIndex={selectedCollatBank?.marketIndex}
						marketDisplaySymbol={
							selectedBankUiConfig.uiSymbols.marketDisplaySymbol
						}
					/>

					<Divider className="bg-stroke-divider" />

					<BalanceChanges
						selectedCollatBank={selectedCollatBank}
						targetAccountKey={targetAccountKeyInput}
						selectedTargetAccount={selectedTargetAccount as AccountData}
						collatTransferAmountBigNum={collatTransferAmountBigNum}
						assetCurrentPrice={oraclePrice}
						baseAssetDisplaySymbol={
							selectedBankUiConfig.uiSymbols.baseAssetDisplaySymbol
						}
					/>
				</BaseModal.Body>
				<BaseModal.Footer className="flex-col items-stretch">
					<Button
						className="w-full"
						size="lg"
						onClick={handleDeposit}
						disabled={!allowConfirmation}
						isLoading={submitting}
						dataTestId="deposit-modal-submit"
					>
						{isBelowMinDeposit
							? `Minimum Initial Deposit: $${MIN_DEPOSIT_AMOUNT_FOR_NEW_ACCT}`
							: 'Deposit'}
					</Button>
					{isEmptyAmplifyAccount && (
						<InlineAlert
							variant="warning"
							title="Depositing into an empty Amplify account is not allowed."
						/>
					)}
					<RentDisclaimers
						accountExists={accountExists}
						totalAccountCreationCost={totalAccountCreationCost}
						hasEnoughSolInWallet={hasEnoughSolInWallet}
						solRequiredToDeposit={solRequiredToDeposit}
						isLoadingWalletBalance={loadingWalletDepositableBalance}
					/>
				</BaseModal.Footer>
			</div>

			{/* Mobile: Deposit from other sources inside modal */}
			{showDepositFromOtherSources && (
				<div
					className={cj(
						isMobile ? 'border-t border-stroke-divider p-lg' : 'pt-xs'
					)}
				>
					<button
						className={cj(
							'flex h-17 w-full items-center gap-sm rounded-sm px-lg text-text-default',
							isMobile
								? 'bg-button-secondary-bg hover:bg-button-secondary-bg-hover'
								: 'bg-container-bg shadow-md hover:bg-input-bg'
						)}
						onClick={handleDepositFromOtherSources}
					>
						<DepositFromOtherSourcesContent />
					</button>
				</div>
			)}
		</BaseModal.Container>
	);
};

export default React.memo(DepositModal);
